import React, { useState, useEffect } from "react";
import { Button, Table, Modal, Spinner, Form, FloatingLabel, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faEye, faDownload } from "@fortawesome/free-solid-svg-icons";
import CampReportModal from "./CampReportModal";
import buttonStyles from "../../styles/Buttons.module.css";
import styles from "../../styles/Modal.module.css";

// Components
import ConfirmModal from "../../components/ConfirmModal";
import AlertModal from "../../components/AlertModal";
import AppNav from "../../header/AppNav";

// Services
import { CampServices } from "../../services/CampService";
import { SSMemberServices } from "../../services/SSMemberService";
import { AshaBlockServices } from "../../services/AshaBlocks";
import { TalukServices } from "../../services/Taluk";
import { PHCServices } from "../../services/Phcs";
import { PatientServices } from "../../services/PatientService";
import { AuthService } from "../../services/AuthService";
import * as XLSX from 'xlsx';

function Camp() {
  // State for data and loading
  const [camps, setCamps] = useState([]);
  const [camp, setCamp] = useState([]);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedCampReport, setSelectedCampReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [modalData, setModalData] = useState({});
  const [deleteData, setDeleteData] = useState({ id: null, name: '' });
  
  // State for form data
  const [formData, setFormData] = useState({
    id: '',
    campName: '',
    campDate: '',
    ashaBlockId: '',
    campLocation: '',
    ssmId: ''
  });
  const [errors, setErrors] = useState({});

  // State for dropdowns
  const [taluks, setTaluks] = useState([]);
  const [phcs, setPhcs] = useState([]);
  const [ashaBlocks, setAshaBlocks] = useState([]);
  const [ssMembers, setSSMembers] = useState([]);
  const [selectedTaluk, setSelectedTaluk] = useState("");
  const [selectedPhc, setSelectedPhc] = useState("");
  const [selectedAshaBlock, setSelectedAshaBlock] = useState("");
  
  // State for search and filtering
  const [searchTerm, setSearchTerm] = useState("");
  
  // User role and authentication
  const roleDetails = AuthService.getUserRole();
  const userRole = roleDetails?.role;
  const user = AuthService.getAuthEntity();

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Fetch data on component mount
  useEffect(() => {
    const initialize = async () => {
      await initializeData();
      await fetchData();
    };
    initialize();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      console.log('Fetching camps...');
      let response;
      
      // Always fetch all camps on page load
      if (userRole === "Super Admins") {
        response = await CampServices.getAllCamps();
      } else if (userRole === "Taluk") {
        const talukId = user.talukDetails?.id;
        response = await CampServices.getCampsByTaluk(talukId);
      } else if (userRole === "PHC") {
        const phcId = user.phcDetails?.id;
        response = await CampServices.getCampsByPhc(phcId);
      } else if (userRole === "Asha Block") {
        const ashaBlockId = user.ashaBlockDetails?.id;
        response = await CampServices.getCampsByAshaBlock(ashaBlockId);
      } else {
        response = await CampServices.getAllCamps();
      }
      
      let campsData = [];
      if (Array.isArray(response)) {
        campsData = response;
      } else if (response && Array.isArray(response.data)) {
        campsData = response.data;
      }
      setCamps(campsData);
    } catch (err) {
      console.error("Error fetching camps:", err);
      setError("Failed to load camps. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const initializeData = async () => {
    try {
      // Always fetch SS Members
      const ssMembersResponse = await SSMemberServices.getAllSSMembers();
      setSSMembers(Array.isArray(ssMembersResponse) ? ssMembersResponse : []);
      
      if (userRole === "Super Admins") {
        await loadAllFilters();
      } else if (userRole === "Taluk") {
        const talukId = user.talukDetails?.id;
        await loadPhcsForTaluk(talukId);
        const blocksRes = await AshaBlockServices.getAshaBlockByTalukId(talukId);
        setAshaBlocks(Array.isArray(blocksRes) ? blocksRes : Object.values(blocksRes || {}));
      } else if (userRole === "PHC") {
        const phcId = user.phcDetails?.id;
        await loadAshaBlocksForPhc(phcId);
      }
    } catch (error) {
      console.error("Error initializing data:", error);
      setModalData({
        title: 'Error',
        message: 'Failed to load dropdown data. Please refresh the page.',
        variant: 'danger'
      });
      setShowAlertModal(true);
    }
  };
  
  const loadAllFilters = async () => {
    try {
      const taluksRes = await TalukServices.getAllTaluks();
      setTaluks(Array.isArray(taluksRes) ? taluksRes : Object.values(taluksRes || {}));
      
      const phcsRes = await PHCServices.getAllPhcs();
      setPhcs(Array.isArray(phcsRes) ? phcsRes : Object.values(phcsRes || {}));
      
      const blocksRes = await AshaBlockServices.getAllAshaBlocks();
      setAshaBlocks(Array.isArray(blocksRes) ? blocksRes : Object.values(blocksRes || {}));
    } catch (error) {
      console.error("Error loading filters:", error);
    }
  };
  
  const loadPhcsForTaluk = async (talukId) => {
    try {
      const phcsRes = await PHCServices.gePhcsByTalukId(talukId);
      setPhcs(Array.isArray(phcsRes) ? phcsRes : Object.values(phcsRes || {}));
    } catch (error) {
      console.error("Error loading PHCs:", error);
    }
  };
  
  const loadAshaBlocksForPhc = async (phcId) => {
    try {
      const blocksRes = await AshaBlockServices.geAshaBlockByPhcId(phcId);
      setAshaBlocks(Array.isArray(blocksRes) ? blocksRes : Object.values(blocksRes || {}));
    } catch (error) {
      console.error("Error loading Asha Blocks:", error);
    }
  };

  // Fetch PHCs when Taluk is selected
  const getPhcs = async (talukId) => {
    try {
      if (!talukId) {
        setPhcs([]);
        return;
      }
      const res = await PHCServices.gePhcsByTalukId(talukId);
      if (Array.isArray(res)) {
        setPhcs(res);
      } else if (res && typeof res === 'object') {
        const values = Object.values(res);
        setPhcs(values.length > 0 && values[0].id ? values : []);
      } else {
        setPhcs([]);
      }
    } catch (error) {
      console.error('Failed to fetch PHCs for Taluk', error);
      setPhcs([]);
    }
  };
    

  // Handle filter changes for Super Admin users - Taluk dropdown
  useEffect(() => {
    if (userRole === "Super Admins") {
      setSelectedPhc("");
      setSelectedAshaBlock("");
      
      if (selectedTaluk) {
        getPhcs(selectedTaluk);
      } else {
        setPhcs([]);
        setSelectedPhc('');
        setAshaBlocks([]);
        setSelectedAshaBlock('');
      }
    }
  }, [selectedTaluk]);
  
  // Handle filter changes for Super Admin and Taluk users - PHC dropdown
  useEffect(() => {
    if (userRole === "Super Admins" || userRole === "Taluk") {
      setSelectedAshaBlock("");
      
      if (selectedPhc) {
        loadAshaBlocksForPhc(selectedPhc);
      }else {
        setAshaBlocks([]);
        setSelectedAshaBlock('');
      }
    }
  }, [selectedPhc]);


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTalukChange = async (e) => {
    const value = e.target.value;
    setSelectedTaluk(value);
    setSelectedPhc('');
    setSelectedAshaBlock('');
    setFormData(prev => ({ ...prev, ashaBlockId: '' }));
    await getPhcs(value);
  };

  const handlePhcChange = async (e) => {
    const value = e.target.value;
    setSelectedPhc(value);
    setSelectedAshaBlock('');
    setFormData(prev => ({ ...prev, ashaBlockId: '' }));
    
    if (value) {
      const blocksRes = await AshaBlockServices.geAshaBlockByPhcId(value);
      setAshaBlocks(Array.isArray(blocksRes) ? blocksRes : Object.values(blocksRes || {}));
    } else {
      setAshaBlocks([]);
    }
  };
  
  
  const handleAshaBlockChange = (e) => {
    const value = e.target.value;
    setSelectedAshaBlock(value);
  };

  // Clear modal filters when modal is closed
  const clearModalFilters = () => {
    setSelectedTaluk('');
    setSelectedPhc('');
    setSelectedAshaBlock('');
    setAshaBlocks([]);
    setFormData({
      id: '',
      campDate: '',
      ashaBlockId: '',
      campLocation: '',
      ssmId: ''
    });
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.ashaBlockId) {
      newErrors.ashaBlockId = 'Please select an Asha Block';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await CampServices.addCamp(formData);
      if (response && (response.status === 200 || response.status === 201)) {
        clearModalFilters();
        setShowAddModal(false);
        setModalData({
          title: 'Success',
          message: 'Camp added successfully!',
          variant: 'success'
        });
        setShowAlertModal(true);
        fetchData();
      }
    } catch (error) {
      console.error('Error adding camp:', error);
      setModalData({
        title: 'Error',
        message: error.message || 'Failed to add camp. Please try again.',
        variant: 'danger'
      });
      setShowAlertModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = async (camp) => {
    try {
      console.log('Editing camp:', camp);
      
      // Reset states first
      setSelectedTaluk('');
      setSelectedPhc('');
      setAshaBlocks([]);
      
      if (camp.ashaBlockDetails?.phcDetails?.talukDetails?.id) {
        // Set the taluk
        const talukId = camp.ashaBlockDetails.phcDetails.talukDetails.id;
        setSelectedTaluk(talukId);
        
        // Fetch PHCs for the selected taluk
        const phcs = await PHCServices.gePhcsByTalukId(talukId);
        setPhcs(Array.isArray(phcs) ? phcs : []);
        
        // Set the PHC after a small delay to ensure PHCs are loaded
        setTimeout(() => {
          if (camp.ashaBlockDetails?.phcDetails?.id) {
            const phcId = camp.ashaBlockDetails.phcDetails.id;
            setSelectedPhc(phcId);
            
            // Fetch Asha Blocks for the selected PHC
            AshaBlockServices.geAshaBlockByPhcId(phcId)
              .then(res => {
                if (Array.isArray(res)) {
                  setAshaBlocks(res);
                } else if (res && typeof res === 'object') {
                  const values = Object.values(res);
                  setAshaBlocks(values.length > 0 && values[0].id ? values : []);
                } else {
                  setAshaBlocks([]);
                }
                
                // Set form data after all data is loaded
                setFormData({
                  id: camp.id,
                  status: camp.status || '',
                  campDate: camp.campDate ? camp.campDate.split('T')[0] : '',
                  ashaBlockId: camp.ashaBlockDetails?.id || '',
                  campLocation: camp.campLocation || '',
                  ssmId: camp.leadScreenMemberDetails?.id || ''
                });
                
                setShowEditModal(true);
              })
              .catch(error => {
                console.error('Failed to fetch Asha Blocks:', error);
                setAshaBlocks([]);
                setShowEditModal(true);
              });
          }
        }, 100);
      } else {
        // If no taluk/PHC data, just set the form and show modal
        setFormData({
          id: camp.id,
          status: camp.status || '',
          campDate: camp.campDate ? camp.campDate.split('T')[0] : '',
          ashaBlockId: camp.ashaBlockDetails?.id || '',
          campLocation: camp.campLocation || '',
          ssmId: camp.leadScreenMemberDetails?.id || ''
        });
        setShowEditModal(true);
      }
    } catch (error) {
      console.error('Error preparing edit form:', error);
      setModalData({
        title: 'Error',
        message: 'Failed to load camp details for editing.',
        variant: 'danger'
      });
      setShowAlertModal(true);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await CampServices.updateCamp(formData);
      if (response && (response.status === 200 || response.status === 204)) {
        setShowEditModal(false);
        setModalData({
          title: 'Success',
          message: 'Camp updated successfully!',
          variant: 'success'
        });
        setShowAlertModal(true);
        fetchData();
      }
    } catch (error) {
      console.error('Error updating camp:', error);
      setModalData({
        title: 'Error',
        message: error.message || 'Failed to update camp. Please try again.',
        variant: 'danger'
      });
      setShowAlertModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (camp) => {
    setDeleteData({ id: camp.id, name: camp.campName });
    setShowConfirmModal(true);
  };

  const handleViewReport = async (camp) => {
    try {
      console.log(camp.id);
      const response = await CampServices.getCampReport(camp.id);
      console.log(response);
      if (response && response.status === 200) {
        setSelectedCampReport({
          campDate: response.data.campDate,
          campLocation: response.data.campLocation,
          suspectCount: response.data.suspectCount || 0,
          presbyopiaCount: response.data.presbyopiaCount || 0,
      powerToPowerCount: response.data.powerToPowerCount || 0,
      cataractSurgeryReferralCount: response.data.cataractSurgeryReferralCount || 0,
      glaucomaDiagnosedCount: response.data.glaucomaDiagnosedCount || 0,
      glaucomaSuspectedCount: response.data.glaucomaSuspectedCount || 0,
      drReferralCount: response.data.drReferralCount || 0,
      otherIssueCount: response.data.otherIssueCount || 0,
      presboypiaSpectsCount: response.data.presboypiaSpectsCount || 0
    }); 
    setCamp(camp);
    setShowReportModal(true);
  }
  } catch (error) {
    console.error('Error fetching camp report:', error);
    setShowReportModal(false);
  }
  };

  const handleDownloadScreeningData = async (camp) => {
    try {
      setLoading(true);
      console.log('Downloading screening data for camp:', camp);
      
      // Get PHC ID from camp data
      const phcId = camp.ashaBlockDetails?.phcDetails?.id;
      
      if (!phcId) {
        setModalData({
          title: 'Error',
          message: 'PHC information not found for this camp.',
          variant: 'danger'
        });
        setShowAlertModal(true);
        return;
      }

      // Fetch patients data using getAllPatientsByPhc API
      const response = await PatientServices.getAllPatientsByPhc(phcId);
      console.log('Patients data response:', response);
      
      let patientsData = [];
      if (Array.isArray(response)) {
        patientsData = response;
      } else if (response && Array.isArray(response.data)) {
        patientsData = response.data;
      } else if (response && response.data) {
        patientsData = [response.data];
      }

      if (patientsData.length === 0) {
        setModalData({
          title: 'Info',
          message: 'No screening data found for this camp.',
          variant: 'info'
        });
        setShowAlertModal(true);
        return;
      }

      // Prepare data for Excel export
      const excelData = patientsData.map((patient, index) => ({
        'Sl No': index + 1,
        'Patient Name': patient.name || '-',
        'Age': patient.age || '-',
        'Gender': patient.gender || '-',
        'Phone Number': patient.mobileNo || '-',
        'Village': patient.ssAshaVillage || '-',
        'Address': patient.address || '-',
        'insurance': patient.insurance || '-',
        'Id Type': patient.idType || '-',
        'Id Number': patient.idNumber || '-',
       'Narega': patient.narega === 1 ? 'Yes' : 'No' || '-',
        'Taluk': patient.ashaBlockDetails.phcDetails.talukDetails.taluk || '-',
        'PHC': patient.ashaBlockDetails.phcDetails.phc || '-',
        'Asha Block': patient.ashaBlockDetails.ashaBlock || '-',
        'Asha': patient.ashaDetails?.name || patient.ssAshaName || '-',
      }));

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Screening Data');
      
      // Generate filename with camp details
      const campDate = new Date(camp.campDate).toLocaleDateString('en-GB').replace(/\//g, '-');
      const filename = `Screening_Data_${camp.campLocation}_${campDate}.xlsx`;
      
      // Download the file
      XLSX.writeFile(workbook, filename);
      
      setModalData({
        title: 'Success',
        message: `Screening data downloaded successfully as ${filename}`,
        variant: 'success'
      });
      setShowAlertModal(true);
      
    } catch (error) {
      console.error('Error downloading screening data:', error);
      setModalData({
        title: 'Error',
        message: 'Failed to download screening data. Please try again.',
        variant: 'danger'
      });
      setShowAlertModal(true);
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    setShowConfirmModal(false);
    try {
      await CampServices.deleteCamp(deleteData.id);
      setModalData({
        title: 'Success',
        message: `Camp "${deleteData.name}" deleted successfully!`,
        variant: 'success'
      });
      setShowAlertModal(true);
      fetchData();
    } catch (error) {
      console.error('Error deleting camp:', error);
      setModalData({
        title: 'Error',
        message: `Failed to delete ${deleteData.name}. Please try again.`,
        variant: 'danger'
      });
      setShowAlertModal(true);
    }
  };
  
  // Filter camps based on search term and selected filters
  const filteredCamps = camps.filter((camp) => {
    // Search functionality
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = (
        (camp.campLocation || "").toLowerCase().includes(searchLower) ||
        (camp.ashaBlockDetails?.ashaBlock || "").toLowerCase().includes(searchLower) ||
        (camp.leadScreenMemberDetails?.name || "").toLowerCase().includes(searchLower) ||
        (camp.status || "").toLowerCase().includes(searchLower) ||
        new Date(camp.campDate).toLocaleDateString('en-GB').toLowerCase().includes(searchLower)
      );
      if (!matchesSearch) return false;
    }
    
    // Filter by selected Taluk for Super Admin users
    if (selectedTaluk && userRole === "Super Admins") {
      if (!camp.ashaBlockDetails?.phcDetails?.talukDetails || 
          camp.ashaBlockDetails.phcDetails.talukDetails.id != selectedTaluk) {
        return false;
      }
    }
    
    // Filter by selected PHC for Super Admin and Taluk users
    if (selectedPhc && (userRole === "Super Admins" || userRole === "Taluk")) {
      if (!camp.ashaBlockDetails?.phcDetails || 
          camp.ashaBlockDetails.phcDetails.id != selectedPhc) {
        return false;
      }
    }
    
    // Filter by selected Asha Block for Super Admin, Taluk, and PHC users
    if (selectedAshaBlock && (userRole === "Super Admins" || userRole === "Taluk" || userRole === "PHC")) {
      if (!camp.ashaBlockDetails || camp.ashaBlockDetails.id != selectedAshaBlock) {
        return false;
      }
    }
    
    return true;
  });
  
  // Render filters based on user role
  const renderFilters = () => {
    return (
      <Row className="mb-3">
        {/* Super Admin filters */}
        {userRole === "Super Admins" && (
          <>
            <Col>
              <FloatingLabel controlId="taluk" label="Taluk" className="mt-3">
                <Form.Select
                  value={selectedTaluk}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedTaluk(value);
                    setSelectedPhc('');
                    setSelectedAshaBlock('');
                    if (value) {
                      getPhcs(value);
                    } else {
                      setPhcs([]);
                    }
                  }}
                >
                  <option value="">All Taluks</option>
                  {taluks.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.taluk}
                    </option>
                  ))}
                </Form.Select>
              </FloatingLabel>
            </Col>
            <Col>
              <FloatingLabel controlId="phc" label="PHC" className="mt-3">
                <Form.Select
                  value={selectedPhc}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedPhc(value);
                    setSelectedAshaBlock('');
                    if (value) {
                      loadAshaBlocksForPhc(value);
                    } else {
                      setAshaBlocks([]);
                    }
                  }}
                >
                  <option value="">All PHCs</option>
                  {phcs.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.phc}
                    </option>
                  ))}
                </Form.Select>
              </FloatingLabel>
            </Col>
          </>
        )}
        
        {/* Taluk user filters */}
        {userRole === "Taluk" && (
          <Col>
            <FloatingLabel controlId="phc" label="PHC" className="mt-3">
              <Form.Select
                value={selectedPhc}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedPhc(value);
                  setSelectedAshaBlock('');
                  if (value) {
                    loadAshaBlocksForPhc(value);
                  } else {
                    setAshaBlocks([]);
                  }
                }}
              >
                <option value="">All PHCs</option>
                {phcs.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.phc}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
          </Col>
        )}
        
        {/* Asha Block filter for Super Admin, Taluk, and PHC users */}
        {(userRole === "Super Admins" || userRole === "Taluk" || userRole === "PHC") && (
          <Col>
            <FloatingLabel controlId="ashaBlock" label="Asha Block" className="mt-3">
              <Form.Select
                value={selectedAshaBlock}
                onChange={handleAshaBlockChange}
              >
                <option value="">All Asha Blocks</option>
                {ashaBlocks.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.ashaBlock}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
          </Col>
        )}
        
        {/* Search bar */}
        <Col>
          <Form.Control
            type="text"
            placeholder="Search camps..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="mt-3"
          />
        </Col>
      </Row>
    );
  };
  return (
    <div>
      <AppNav />
          <h2 className="list-style">Secondary Screening Camps</h2>
          <div className="list-table-style">
          <div className="add-button mb-3"> 
          <Button 
            variant="primary"
            onClick={() => {
              // Reset all form fields
              setFormData({
                id: '',
                campDate: '',
                ashaBlockId: '',
                campLocation: '',
                ssmId: '',
                status: ''
              });
              // Reset dropdown selections
              setSelectedTaluk('');
              setSelectedPhc('');
              setAshaBlocks([]);
              // Show the modal
              setShowAddModal(true);
            }}
            className={buttonStyles.primaryButton}
          >
            Add New Camp
          </Button>
          </div>
          </div>

        {/* Filters and Search */}
        {renderFilters()}
        
        {/* Main Content */}
        {loading ? (
          <div className="text-center p-5">
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                      <p className="mt-2">Loading Camps...</p>
                    </div>
        ) : error ? (
          <div className="alert alert-danger" role="alert">{error}</div>
        ) : (
          <div className="table-responsive">
            <Table striped bordered hover className="align-middle">
              {/* Table Header */}
              <thead className="table-light">
                <tr>
                  <th>SL No</th>
                  <th>Date</th>
                  <th>Location</th>
                  <th>ASHA Block</th>
                  <th>Team Member</th>
                  <th>Status</th>
                  <th>Work Sheet</th>
                  <th>Abstract Report</th>
                  <th>Actions</th>
                </tr>
              </thead>
              {/* Table Body */}
              <tbody>
                {filteredCamps.map((camp, index) => (
                  <tr key={camp.id}>
                    <td>{index + 1}</td>
                    <td>{new Date(camp.campDate).toLocaleDateString('en-GB')}</td>
                    <td>{camp.campLocation}</td>
                    <td>{camp.ashaBlockDetails?.ashaBlock || 'N/A'}</td>
                    <td>{camp.leadScreenMemberDetails?.name || 'N/A'}</td>
                    <td>{camp.status}</td>
                    <td><Button
                        variant="link"
                        size="sm"
                        onClick={() => handleDownloadScreeningData(camp)}
                        style={{ boxShadow: 'none' }}
                        className='text-muted'
                        title="Download screening data to Excel"
                        disabled={loading}
                      >
                        <FontAwesomeIcon icon={faDownload} style={{ color: '#05275a' }} />
                      </Button></td>
                    <td><Button
                        variant="link"
                        size="sm"
                        onClick={() => handleViewReport(camp)}
                        style={{ boxShadow: 'none' }}
                        className='text-muted'
                      >
                      <FontAwesomeIcon 
                        icon={faEye} 
                        style={{ color: '#05275a', cursor: 'pointer' }}
                      />
                      </Button></td>
                    <td>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => handleEditClick(camp)}
                        style={{ boxShadow: 'none' }}
                        disabled={camp.status === 'Completed'}
                        className={camp.status === 'Completed' ? 'text-muted' : ''}
                        title={camp.status === 'Completed' ? 'Cannot edit completed camps' : 'Edit camp'}
                      >
                        <FontAwesomeIcon icon={faEdit} style={{ color: '#05275a' }} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {/* Add Camp Modal */}
        <Modal
          show={showAddModal}
          onHide={() => {
            if (!loading) {
              clearModalFilters();
              setShowAddModal(false);
            }
          }}
          size="lg"
          centered
          backdrop={loading ? 'static' : true}
          keyboard={!loading}
          className={styles.modal}
        >
          <Form onSubmit={handleAddSubmit} className={styles.modalForm}>
            <Modal.Header closeButton={!loading} closeVariant={loading ? 'white' : undefined} className={styles.modalHeader}>
              <Modal.Title className={styles.modalTitle}>Add New Camp</Modal.Title>
            </Modal.Header>
            <Modal.Body className={styles.modalBody}>
              {loading ? (
                <div className="text-center p-3">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Saving...</span>
                  </Spinner>
                  <p className="mt-2">Saving camp details...</p>
                </div>
              ) : (
                <>
                  <Row className="mb-3">
                    <Col md={6}>
                      <FloatingLabel controlId="campDate" label={
                        <>
                          Camp Date <span className="text-danger">*</span>
                        </>
                      }>
                        <Form.Control 
                          type="date" 
                          name="campDate"
                          value={formData.campDate}
                          onChange={handleInputChange}
                          required
                          style={{ height: '58px' }}
                          className="align-items-center d-flex"
                        />
                        <Form.Control.Feedback type="invalid">
                          Please select a camp date
                        </Form.Control.Feedback>
                      </FloatingLabel>
                    </Col>
                    <Col md={6}>
                      <FloatingLabel controlId="ssmId" label="Team Member">
                        <Form.Select 
                          value={formData.ssmId}
                          onChange={handleInputChange}
                          name="ssmId"
                          required
                          style={{ height: '58px' }}
                          className="align-items-center d-flex"
                        >
                          <option value="">Select Team Member</option>
                          {ssMembers.map(member => (
                            <option key={member.id} value={member.id}>
                              {member.name}
                            </option>
                          ))}
                        </Form.Select>
                      </FloatingLabel>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={12}>
                      <FloatingLabel controlId="campLocation" label={
                        <>
                          Location <span className="text-danger">*</span>
                        </>
                      }>
                        <Form.Control 
                          as="textarea" 
                          name="campLocation"
                          value={formData.campLocation}
                          onChange={handleInputChange}
                          style={{ height: '58px', minHeight: '58px' }}
                          className="align-items-center d-flex"
                          required 
                        />
                        <Form.Control.Feedback type="invalid">
                          Please enter a camp location
                        </Form.Control.Feedback>
                      </FloatingLabel>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={4}>
                      <FloatingLabel controlId="taluk" label="Taluk*">
                        <Form.Select 
                          value={selectedTaluk}
                          onChange={handleTalukChange}
                          required
                        >
                          <option value="" disabled hidden>Select</option>
              {taluks.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.taluk}
                </option>
              ))}
                        </Form.Select>
                      </FloatingLabel>
                    </Col>
                    <Col md={4}>
                      <FloatingLabel controlId="phc" label="PHC*">
                        <Form.Select 
                          value={selectedPhc}
                          onChange={handlePhcChange}
                          disabled={!selectedTaluk}
                          required
                        >
                         <option value="" disabled hidden>Select</option>
              {phcs
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.phc}
                  </option>
                ))}
                        </Form.Select>
                      </FloatingLabel>
                    </Col>
                    <Col md={4}>
                      <FloatingLabel controlId="ashaBlockId" label={
                        <>
                          Asha Block <span className="text-danger">*</span>
                        </>
                      }>
                        <Form.Select 
                          value={formData.ashaBlockId}
                          onChange={handleInputChange}
                          name="ashaBlockId"
                          disabled={!selectedPhc}
                          required
                        >
                          <option value="">Select Asha Block</option>
                          {ashaBlocks.map((block) => (
                            <option key={block.id} value={block.id}>
                              {block.ashaBlock}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          Please select an Asha Block
                        </Form.Control.Feedback>
                      </FloatingLabel>
                    </Col>
                  </Row>
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button 
                variant="success" 
                type="submit"
                disabled={loading}
                className={buttonStyles.primaryButton}
              >
                {loading ? 'Saving...' : 'Save Camp'}
              </Button>
              <Button 
                variant="danger" 
                onClick={() => {
                  clearModalFilters();
                  setShowAddModal(false);
                }}
                disabled={loading}
                className={buttonStyles.dangerButton}
              >
                Cancel
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Edit Camp Modal */}
        <Modal
          show={showEditModal}
          onHide={() => !loading && setShowEditModal(false)}
          size="lg"
          centered
          backdrop={loading ? 'static' : true}
          className={styles.modal}
        >
          <Form onSubmit={handleEditSubmit} className={styles.modalForm}>
            <Modal.Header closeButton={!loading} closeVariant={loading ? 'white' : undefined} className={styles.modalHeader}>
              <Modal.Title className={styles.modalTitle}>Edit Camp</Modal.Title>
            </Modal.Header>
            <Modal.Body className={styles.modalBody}>
              {loading ? (
                <div className="text-center p-3">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Updating...</span>
                  </Spinner>
                  <p className="mt-2">Updating camp details...</p>
                </div>
              ) : (
                <>
                  <Row className="mb-3">
                    <Col md={6}>
                      <FloatingLabel controlId="campDate" label={
                        <>
                          Camp Date <span className="text-danger">*</span>
                        </>
                      }>
                        <Form.Control 
                          type="date" 
                          name="campDate"
                          value={formData.campDate}
                          onChange={handleInputChange}
                          required
                          style={{ height: '58px' }}
                          className="align-items-center d-flex"
                        />
                        <Form.Control.Feedback type="invalid">
                          Please select a camp date
                        </Form.Control.Feedback>
                      </FloatingLabel>
                    </Col>
                    <Col md={6}>
                      <FloatingLabel controlId="ssmId" label="Team Member">
                        <Form.Select 
                          value={formData.ssmId}
                          onChange={handleInputChange}
                          name="ssmId"
                          required
                          style={{ height: '58px' }}
                          className="align-items-center d-flex"
                        >
                          <option value="">Select Team Member</option>
                          {ssMembers.map(member => (
                            <option key={member.id} value={member.id}>
                              {member.name}
                            </option>
                          ))}
                        </Form.Select>
                      </FloatingLabel>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={12}>
                      <FloatingLabel controlId="campLocation" label={
                        <>
                          Location <span className="text-danger">*</span>
                        </>
                      }>
                        <Form.Control 
                          as="textarea" 
                          name="campLocation"
                          value={formData.campLocation}
                          onChange={handleInputChange}
                          style={{ height: '58px', minHeight: '58px' }}
                          className="align-items-center d-flex"
                          required 
                        />
                        <Form.Control.Feedback type="invalid">
                          Please enter a camp location
                        </Form.Control.Feedback>
                      </FloatingLabel>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={6}>
                      <FloatingLabel controlId="status" label="Status">
                        <Form.Control 
                          type="text" 
                          name="status"
                          value={formData.status}
                          readOnly
                          style={{ 
                            height: '58px',
                            backgroundColor: '#e9ecef',
                            cursor: 'not-allowed'
                          }}
                          className="align-items-center d-flex"
                        />
                      </FloatingLabel>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={4}>
                      <FloatingLabel controlId="taluk" label="Taluk">
                        <Form.Select 
                          value={selectedTaluk}
                          onChange={handleTalukChange}
                          required
                          style={{ height: '58px' }}
                          className="align-items-center d-flex"
                        >
                          <option value="" disabled hidden>Select</option>
                          {taluks.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.taluk}
                            </option>
                          ))}
                        </Form.Select>
                      </FloatingLabel>
                    </Col>
                    <Col md={4}>
                      <FloatingLabel controlId="phc" label="PHC">
                        <Form.Select 
                          value={selectedPhc}
                          onChange={handlePhcChange}
                          disabled={!selectedTaluk}
                          required
                          style={{ height: '58px' }}
                          className="align-items-center d-flex"
                        >
                          <option value="" disabled hidden>Select</option>
                          {phcs.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.phc}
                            </option>
                          ))}
                        </Form.Select>
                      </FloatingLabel>
                    </Col>
                    <Col md={4}>
                      <FloatingLabel controlId="ashaBlockId" label="Asha Block">
                        <Form.Select 
                          value={formData.ashaBlockId}
                          onChange={handleInputChange}
                          name="ashaBlockId"
                          disabled={!selectedPhc}
                          required
                          style={{ height: '58px' }}
                          className={`align-items-center d-flex ${errors.ashaBlockId ? 'is-invalid' : ''}`}
                        >
                          <option value="" disabled hidden>Select Asha Block</option>
                          {ashaBlocks.map((block) => (
                            <option key={block.id} value={block.id}>
                              {block.ashaBlock}
                            </option>
                          ))}
                        </Form.Select>
                        {errors.ashaBlockId && (
                          <div className="invalid-feedback">
                            {errors.ashaBlockId}
                          </div>
                        )}
                      </FloatingLabel>
                    </Col>
                  </Row>
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button 
                variant="success" 
                type="submit"
                disabled={loading}
                className={buttonStyles.primaryButton}
              >
                {loading ? 'Updating...' : 'Update Camp'}
              </Button>
               <Button 
                variant="danger" 
                onClick={() => setShowEditModal(false)}
                disabled={loading}
                className={buttonStyles.dangerButton}
              >
                Cancel
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Confirmation Modal */}
        <ConfirmModal
          show={showConfirmModal}
          onHide={() => setShowConfirmModal(false)}
          onConfirm={confirmDelete}
          title="Confirm Delete"
          message={`Are you sure you want to delete "${deleteData.name}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
        />

        {/* Alert Modal */}
        <AlertModal
          show={showAlertModal}
          onHide={() => setShowAlertModal(false)}
          title={modalData.title}
          message={modalData.message}
          variant={modalData.variant || 'info'}
        />
    
    <CampReportModal 
  show={showReportModal} 
  onClose={() => setShowReportModal(false)} 
  report={selectedCampReport}
  camp={camp} 
/>
    </div>
  );
}

export default Camp;
